import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/api";
import PageHero from "@/components/tex/PageHero";
import AnimatedIndiaMap from "@/components/tex/AnimatedIndiaMap";
import { staggerContainer, staggerItem } from "@/components/tex/PageTransition";

export default function CoveragePage() {
  const { openQuote } = useOutletContext();
  const [coverage, setCoverage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Coverage — Texcheme";
    window.scrollTo(0, 0);
    api
      .getCoverage()
      .then(setCoverage)
      .catch((e) => setError(e.message));
  }, []);

  return (
    <>
      <PageHero
        eyebrow="Where we serve"
        title="Pan-India dispatch from six warehouses"
        lead={coverage?.summary}
      />

      <section className="panel coverage-map-panel" data-testid="coverage-map-panel">
        <div className="wrap coverage-map-grid">
          <div>
            <div className="eyebrow" style={{ color: "var(--brand-light)" }}>Live dispatch network</div>
            <h2 style={{ color: "#fff", fontSize: 24, marginTop: 6 }}>Where our tankers roll</h2>
            <p className="note" style={{ color: "#c7cdc9", maxWidth: 380 }}>
              Illustrative map — pulsing points mark our dispatch depots; dashed lines are typical
              tanker routes to serviced regions. Confirm exact lead times per pincode with sales.
            </p>
          </div>
          <AnimatedIndiaMap />
        </div>
      </section>

      <section className="panel" data-testid="coverage-regions">
        <motion.div
          className="wrap coverage-grid"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <div>
            <div className="panel-head" style={{ marginBottom: 20 }}>
              <div className="eyebrow">States served</div>
              <h2>Regions</h2>
            </div>
            {error && <p className="note">Couldn&apos;t load coverage right now. {error}</p>}
            <div className="region-list">
              {(coverage?.regions || []).map((r, i) => (
                <motion.div
                  className="region-row"
                  key={i}
                  data-testid={`region-${i}`}
                  variants={staggerItem}
                >
                  <div className="region-state">{r.state}</div>
                  <div className="region-note">{r.note}</div>
                </motion.div>
              ))}
            </div>
            <p className="note" style={{ marginTop: 16 }}>
              Regions and depots shown are placeholders — confirm real service geography and lead
              times with sales before relying on them.
            </p>
          </div>

          <aside>
            <div className="panel-head" style={{ marginBottom: 20 }}>
              <div className="eyebrow">Dispatch points</div>
              <h2>Depots</h2>
            </div>
            <div className="depot-list">
              {(coverage?.depots || []).map((d, i) => (
                <motion.div
                  className="depot-card"
                  key={i}
                  data-testid={`depot-${i}`}
                  variants={staggerItem}
                >
                  <h3>{d.name}</h3>
                  <div className="depot-city">{d.city}</div>
                  <p className="note">{d.note}</p>
                </motion.div>
              ))}
            </div>
          </aside>
        </motion.div>
      </section>

      <section className="panel dark" data-testid="coverage-industries">
        <div className="wrap">
          <div className="panel-head">
            <div className="eyebrow">Who we supply</div>
            <h2>Industries we serve</h2>
          </div>
          <div className="industry-grid">
            {(coverage?.industries || []).map((ind, i) => (
              <div className="industry-chip" key={i}>{ind}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="panel cta-panel" data-testid="coverage-cta">
        <div className="wrap cta-panel-inner">
          <div>
            <h2>Not sure if we reach your plant?</h2>
            <p>Send your location and monthly tonnage — we&apos;ll confirm lead time and pricing.</p>
          </div>
          <button className="btn btn-brand" onClick={() => openQuote("coverage")}>Request a quote</button>
        </div>
      </section>
    </>
  );
}
